import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, DimensionId, Note, NoteColor } from '../types';
import {
  nextCardIdRef,
  nextNoteIdRef,
  nextZIndexRef,
  syncIdCounters,
} from '../data';
import api from '../../../services/api';

type RawNote = Omit<Note, 'createdAt'> & { createdAt: string };
type RawCard = Omit<Card, 'id' | 'createdAt' | 'notes'> & {
  cardId: number;
  createdAt: string;
  notes: RawNote[];
};

const parseCard = (raw: RawCard): Card => {
  const { cardId, createdAt, notes, ...rest } = raw;
  return {
    ...rest,
    id: cardId,
    createdAt: new Date(createdAt),
    notes: (notes || []).map(n => ({
      ...n,
      createdAt: new Date(n.createdAt),
    })),
  };
};

export interface CardsState {
  cards: Card[];
  createCard: (q: string, from: DimensionId) => number;
  deleteCard: (id: number) => Promise<void>;
  toggleCardValue: (cardId: number, valueName: string) => void;
  setCardRating: (cardId: number, rating: number) => void;
  finalizeCard: (card: Card) => Promise<Card>;
  markTodoCreated: (cardId: number) => void;
  addNote: (cardId: number) => number;
  updateNote: (
    cardId: number,
    noteId: number,
    text: string,
    html: string,
  ) => void;
  deleteNote: (cardId: number, noteId: number) => void;
  setNotePosition: (
    cardId: number,
    noteId: number,
    x: number,
    y: number,
  ) => void;
  resizeNote: (
    cardId: number,
    noteId: number,
    width: number,
    height: number,
  ) => void;
  setNoteColor: (cardId: number, noteId: number, color: NoteColor) => void;
  bumpNoteZ: (cardId: number, noteId: number) => void;
}

const autosaveDelay = 800;

const draftBodyKey = (card: Card): string =>
  JSON.stringify({
    q: card.q,
    from: card.from,
    rot: card.rot,
    finalized: card.finalized,
    values: card.values,
    rating: card.rating,
    todoCreated: card.todoCreated,
    notes: card.notes,
  });

export const useCardsState = (
  usergroupId: number | undefined,
  onCardIdRemap?: (oldId: number, newId: number) => void,
): CardsState => {
  const [cards, setCards] = useState<Card[]>([]);
  const cardsRef = useRef<Card[]>(cards);
  const persistedIdsRef = useRef<Set<number>>(new Set());
  const lastSavedRef = useRef<Record<number, string>>({});
  const saveTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>(
    {},
  );
  const settlingRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  useEffect(() => {
    if (!usergroupId) return;

    const fetchCards = async (): Promise<void> => {
      try {
        const { data } = await api.get<RawCard[]>('/spinrad', {
          params: { usergroupId },
        });
        const parsed = data.map(parseCard);
        syncIdCounters(parsed);
        persistedIdsRef.current = new Set(parsed.map(c => c.id));
        lastSavedRef.current = Object.fromEntries(
          parsed.map(c => [c.id, draftBodyKey(c)]),
        );
        setCards(parsed);
      } catch (error) {
        console.error('Failed to load Spinrad cards', error);
      }
    };
    fetchCards();
  }, [usergroupId]);

  const deleteCard = useCallback(async (id: number): Promise<void> => {
    if (saveTimersRef.current[id]) {
      clearTimeout(saveTimersRef.current[id]);
      delete saveTimersRef.current[id];
    }
    delete lastSavedRef.current[id];
    if (persistedIdsRef.current.has(id)) {
      await api.delete(`/spinrad/${id}`);
      persistedIdsRef.current.delete(id);
    }
    setCards(prev => prev.filter(c => c.id !== id));
  }, []);

  const toggleCardValue = useCallback(
    (cardId: number, valueName: string): void => {
      setCards(prev =>
        prev.map(c => {
          if (c.id !== cardId) return c;
          const has = c.values.includes(valueName);
          return {
            ...c,
            values: has
              ? c.values.filter(v => v !== valueName)
              : [...c.values, valueName],
          };
        }),
      );
    },
    [],
  );

  const setCardRating = useCallback((cardId: number, rating: number): void => {
    setCards(prev => prev.map(c => (c.id === cardId ? { ...c, rating } : c)));
  }, []);

  const persistCard = useCallback(async (card: Card): Promise<Card> => {
    const body = {
      usergroupId: card.usergroupId,
      q: card.q,
      from: card.from,
      rot: card.rot,
      finalized: card.finalized,
      values: card.values,
      rating: card.rating,
      todoCreated: card.todoCreated,
      notes: card.notes,
    };

    const { data } = persistedIdsRef.current.has(card.id)
      ? await api.put<RawCard>(`/spinrad/${card.id}`, body)
      : await api.post<RawCard>('/spinrad', body);

    const saved = parseCard(data);
    persistedIdsRef.current.add(saved.id);
    return saved;
  }, []);

  const finalizeCard = useCallback(
    async (card: Card): Promise<Card> => {
      if (saveTimersRef.current[card.id]) {
        clearTimeout(saveTimersRef.current[card.id]);
        delete saveTimersRef.current[card.id];
      }
      const saved = await persistCard({ ...card, finalized: true });
      lastSavedRef.current[saved.id] = draftBodyKey(saved);
      if (saved.id !== card.id) delete lastSavedRef.current[card.id];
      setCards(prev => prev.map(c => (c.id === card.id ? saved : c)));
      return saved;
    },
    [persistCard],
  );

  // Persists a draft card and keeps re-checking for further edits made
  // while the request was in flight, one at a time per card. A single
  // in-flight guard (settlingRef) is essential: without it, an edit made
  // before the initial create-POST resolves would still see the card's
  // local id as "not yet persisted" and fire a second POST, creating a
  // duplicate card on the server instead of updating the first one.
  const settleCard = useCallback(
    async (id: number): Promise<void> => {
      if (settlingRef.current.has(id)) return;
      settlingRef.current.add(id);
      let currentId = id;
      try {
        for (;;) {
          const lookupId = currentId;
          const card = cardsRef.current.find(c => c.id === lookupId);
          if (!card || card.finalized) break;
          const key = draftBodyKey(card);
          if (
            lastSavedRef.current[lookupId] === key &&
            persistedIdsRef.current.has(lookupId)
          ) {
            break;
          }
          lastSavedRef.current[lookupId] = key;
          if (saveTimersRef.current[lookupId]) {
            clearTimeout(saveTimersRef.current[lookupId]);
            delete saveTimersRef.current[lookupId];
          }
          // eslint-disable-next-line no-await-in-loop
          const saved = await persistCard(card);
          if (saved.id !== lookupId) {
            delete lastSavedRef.current[lookupId];
            lastSavedRef.current[saved.id] = draftBodyKey(saved);
            setCards(prev => prev.map(c => (c.id === lookupId ? saved : c)));
            onCardIdRemap?.(lookupId, saved.id);
            currentId = saved.id;
          }
        }
      } catch (error) {
        delete lastSavedRef.current[currentId];
        console.error('Failed to autosave draft Spinrad card', error);
      } finally {
        settlingRef.current.delete(id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistCard],
  );

  const createCard = useCallback(
    (q: string, from: DimensionId): number => {
      const id = nextCardIdRef();
      const newCard: Card = {
        id,
        usergroupId: usergroupId as number,
        q,
        from,
        createdAt: new Date(),
        rot: (Math.random() - 0.5) * 2.4,
        finalized: false,
        values: [],
        rating: 0,
        todoCreated: false,
        notes: [],
      };
      // Keep the ref in sync synchronously (the effect that mirrors `cards`
      // into cardsRef only runs after render) so settleCard, called right
      // below, can see this card immediately instead of finding nothing.
      cardsRef.current = [...cardsRef.current, newCard];
      setCards(cardsRef.current);
      // Save immediately rather than waiting for the debounce window, so a
      // card created right before the user navigates away is already
      // persisted instead of racing the next mount's fetch.
      settleCard(id);
      return id;
    },
    [usergroupId, settleCard],
  );

  // Autosave drafts so an unfinished card survives navigating away and back.
  useEffect(() => {
    cards.forEach(card => {
      if (card.finalized) return;
      const key = draftBodyKey(card);
      if (
        lastSavedRef.current[card.id] === key &&
        persistedIdsRef.current.has(card.id)
      ) {
        return;
      }

      if (saveTimersRef.current[card.id]) {
        clearTimeout(saveTimersRef.current[card.id]);
      }
      saveTimersRef.current[card.id] = setTimeout(() => {
        delete saveTimersRef.current[card.id];
        settleCard(card.id);
      }, autosaveDelay);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, settleCard]);

  // Flush any pending debounced save immediately instead of leaving it to
  // fire later in the background, which races with the next mount's fetch
  // and can create a duplicate draft on the server.
  useEffect(
    () => () => {
      Object.keys(saveTimersRef.current).forEach(idKey => {
        const id = Number(idKey);
        clearTimeout(saveTimersRef.current[id]);
        delete saveTimersRef.current[id];
        settleCard(id);
      });
    },
    [settleCard],
  );

  const markTodoCreated = useCallback((cardId: number): void => {
    setCards(prev =>
      prev.map(c => (c.id === cardId ? { ...c, todoCreated: true } : c)),
    );
  }, []);

  const addNote = useCallback((cardId: number): number => {
    const id = nextNoteIdRef();
    const note: Note = {
      id,
      text: '',
      html: '',
      color: 'default',
      x: 60 + Math.floor(Math.random() * 5) * 70,
      y: 80 + Math.floor(Math.random() * 4) * 80,
      rot: (Math.random() - 0.5) * 4,
      z: nextZIndexRef(),
      createdAt: new Date(),
    };
    setCards(prev =>
      prev.map(c =>
        c.id === cardId ? { ...c, notes: [...c.notes, note] } : c,
      ),
    );
    return id;
  }, []);

  const updateNote = useCallback(
    (cardId: number, noteId: number, text: string, html: string): void => {
      setCards(prev =>
        prev.map(c =>
          c.id === cardId
            ? {
                ...c,
                notes: c.notes.map(n =>
                  n.id === noteId ? { ...n, text, html } : n,
                ),
              }
            : c,
        ),
      );
    },
    [],
  );

  const deleteNote = useCallback((cardId: number, noteId: number): void => {
    setCards(prev =>
      prev.map(c =>
        c.id === cardId
          ? { ...c, notes: c.notes.filter(n => n.id !== noteId) }
          : c,
      ),
    );
  }, []);

  const setNotePosition = useCallback(
    (cardId: number, noteId: number, x: number, y: number): void => {
      setCards(prev =>
        prev.map(c =>
          c.id === cardId
            ? {
                ...c,
                notes: c.notes.map(n => (n.id === noteId ? { ...n, x, y } : n)),
              }
            : c,
        ),
      );
    },
    [],
  );

  const resizeNote = useCallback(
    (cardId: number, noteId: number, width: number, height: number): void => {
      setCards(prev =>
        prev.map(c =>
          c.id === cardId
            ? {
                ...c,
                notes: c.notes.map(n =>
                  n.id === noteId ? { ...n, width, height } : n,
                ),
              }
            : c,
        ),
      );
    },
    [],
  );

  const setNoteColor = useCallback(
    (cardId: number, noteId: number, color: NoteColor): void => {
      setCards(prev =>
        prev.map(c =>
          c.id === cardId
            ? {
                ...c,
                notes: c.notes.map(n =>
                  n.id === noteId ? { ...n, color } : n,
                ),
              }
            : c,
        ),
      );
    },
    [],
  );

  const bumpNoteZ = useCallback((cardId: number, noteId: number): void => {
    const z = nextZIndexRef();
    setCards(prev =>
      prev.map(c =>
        c.id === cardId
          ? {
              ...c,
              notes: c.notes.map(n => (n.id === noteId ? { ...n, z } : n)),
            }
          : c,
      ),
    );
  }, []);

  return {
    cards,
    createCard,
    deleteCard,
    toggleCardValue,
    setCardRating,
    finalizeCard,
    markTodoCreated,
    addNote,
    updateNote,
    deleteNote,
    setNotePosition,
    resizeNote,
    setNoteColor,
    bumpNoteZ,
  };
};
